import * as React from 'react';
import { cn } from '../../../utils';

export interface ZineStamp {
  label: React.ReactNode;
  /** ink-filled stamp (spec `.stamp.solid`), e.g. the "~35 min" stamp */
  solid?: boolean;
}

export interface ZineCoverProps {
  /** masthead brand, left side (rendered bold, wide letter-spacing) */
  brand: React.ReactNode;
  /** masthead right-side text (program / level); omitted when absent */
  mastheadRight?: React.ReactNode;
  /** "Class Nº 12 · Unit 3 — Voice at Work" line */
  issueLine?: React.ReactNode;
  /** cover headline; its last word is rendered in outline style */
  title: string;
  /** serif italic standfirst below the headline */
  dek?: React.ReactNode;
  /** pill stamps row (duration / activities / teacher) */
  stamps?: ZineStamp[];
  /** extra slot rendered at the end of the masthead row (restart action etc.) */
  headerExtras?: React.ReactNode;
  /**
   * The class image (the old class page showed it as a banner fading into
   * the background). Here it is a photo print pasted on the cover: cream
   * frame, two tape strips, a caption row underneath.
   */
  imageUrl?: string | null;
  /** italic caption under the print, left side */
  imageCaption?: React.ReactNode;
  /** small red label under the print, right side ("Class 04") */
  imageLabel?: React.ReactNode;
  className?: string;
}

/**
 * Red halftone magazine cover: tape deco, masthead, issue line, headline
 * with the last word in outline style, serif dek and a row of stamps. With
 * an `imageUrl` the class picture is pasted on the right as a photo print
 * (above the headline on narrow screens).
 */
export function ZineCover({
  brand,
  mastheadRight,
  issueLine,
  title,
  dek,
  stamps,
  headerExtras,
  imageUrl,
  imageCaption,
  imageLabel,
  className,
}: ZineCoverProps) {
  const words = title.trim().split(/\s+/);
  const lastWord = words.length > 0 ? words[words.length - 1] : '';
  const head = words.slice(0, -1).join(' ');

  return (
    <header className={cn('z-cover', className)}>
      <div className="z-tape" aria-hidden="true" />
      <div className="z-page">
        <div className="z-masthead">
          <span className="z-brand">
            <b>{brand}</b>
          </span>
          {mastheadRight != null && <span>{mastheadRight}</span>}
          {headerExtras}
        </div>
        <div className={cn('z-cover-body', imageUrl && 'z-cover-body--print')}>
          <div className="z-cover-text">
            {issueLine != null && <p className="z-cover-issue">{issueLine}</p>}
            <h1>
              {head ? `${head} ` : null}
              {lastWord ? <span className="z-outline-word">{lastWord}</span> : null}
            </h1>
            {dek != null && <p className="z-dek">{dek}</p>}
            {stamps != null && stamps.length > 0 && (
              <div className="z-cover-meta">
                {stamps.map((stamp, i) => (
                  <span
                    key={i}
                    className={cn('z-stamp', stamp.solid && 'z-stamp--solid')}
                  >
                    {stamp.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          {imageUrl && (
            <figure className="z-cover-print" data-testid="zine-cover-print">
              <div className="z-cover-print-paper">
                <img src={imageUrl} alt="" loading="eager" />
                {(imageCaption != null || imageLabel != null) && (
                  <figcaption>
                    {imageCaption != null && <em>{imageCaption}</em>}
                    {imageLabel != null && <span>{imageLabel}</span>}
                  </figcaption>
                )}
              </div>
            </figure>
          )}
        </div>
      </div>
    </header>
  );
}
